import React, { useEffect, useRef, useState } from "react";
import { Card, Button, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import CustomSwitch from "components/CustomSwitch";

import {
  userSelector,
  clearState,
  getDietChartSettings,
  updateDietChartSettings,
} from "store/features/authSlice";

const DietChartCreationLayoutToggle = () => {
  const [checked, setChecked] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();
  const { uid, dietChartSettings, isSuccess, isError, message } =
    useSelector(userSelector);

  useEffect(() => {
    if (uid) dispatch(getDietChartSettings({ uid }));
  }, [dispatch, uid]);

  useEffect(() => {
    if (dietChartSettings?.dietChartCreationLayoutType) {
      setChecked(dietChartSettings.dietChartCreationLayoutType === "New");
    }
  }, [dietChartSettings]);

  const toastShownRef = useRef(false);

  useEffect(() => {
    if ((isSuccess || isError) && !toastShownRef.current) {
      toast[isSuccess ? "success" : "error"](message);
      toastShownRef.current = true;

      dispatch(clearState());
    }
  }, [isSuccess, isError, message, dispatch]);

  const handleToggle = () => setChecked((prev) => !prev);

  const handleSave = async () => {
    setIsSaving(true);
    const layoutType = checked ? "New" : "Old";

    await dispatch(
      updateDietChartSettings({
        newDietChartSettings: {
          ...dietChartSettings,
          dietChartCreationLayoutType: layoutType,
        },
        uid,
      })
    );

    setIsSaving(false);
    setIsEditMode(false);
  };

  return (
    <Card sx={{ height: "100%", width: "100%", overflow: "initial" }}>
      <SuiBox
        display="flex"
        flexDirection="column"
        padding={2}
        gap={1}
        width="100%"
        mt={2.5}
        height="100%"
        justifyContent="space-between"
      >
        <SuiTypography mb={1} variant="h6" textAlign="left">
          Create Diet Chart Layout
        </SuiTypography>

        <SuiBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <SuiTypography>Standard</SuiTypography>
          <CustomSwitch
            checked={checked}
            onChange={handleToggle}
            disabled={!isEditMode}
          />
          <SuiTypography>Advanced</SuiTypography>
        </SuiBox>

        <SuiBox mt={3}>
          <SuiButton
            component={Button}
            color="dark"
            variant="gradient"
            onClick={isEditMode ? handleSave : () => setIsEditMode(true)}
            fullWidth
            disabled={isSaving}
          >
            {isEditMode && isSaving ? (
              <>
                Save &nbsp; <CircularProgress color="white" size="1rem" />
              </>
            ) : isEditMode ? (
              "Save"
            ) : (
              "Edit"
            )}
          </SuiButton>
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default DietChartCreationLayoutToggle;
